<template>
  <ResponsiveModal :withClose="false" :showFooter="false">
    <template #content>
      <div class="p-4 gap-2 flex flex-col">
        <Button
          v-if="canCreateAssetGroups"
          @click="handleOpenAddDrawer(true)"
          size="xl"
          variant="secondary"
          type="gray"
          :label="t('components.buttons.add_asset_group')"
        >
          <template #start>
            <Icon name="plus" />
          </template>
        </Button>
        <Button
          v-if="canCreateAssetGroups"
          @click="emit('duplicate:group', item.id)"
          size="xl"
          variant="secondary"
          type="gray"
          :label="t('components.buttons.duplicate_asset_group')"
        >
          <template #start>
            <Icon name="copy" />
          </template>
        </Button>
        <Button
          v-if="canUpdateAssetGroups"
          @click="handleOpenEditDrawer(true)"
          size="xl"
          variant="secondary"
          type="gray"
          :label="t('components.buttons.edit_asset_group')"
        >
          <template #start>
            <Icon name="edit-pen" />
          </template>
        </Button>
        <Button
          v-if="canDeleteAssetGroups"
          @click="handleOpenConfirmDeleteDrawer(true)"
          size="xl"
          variant="primary"
          type="destructive"
          :label="t('components.buttons.delete')"
        >
        </Button>
      </div>

      <Delete
        v-if="item"
        :open="isConfirmDeleteDrawerOpen"
        @update:open="handleOpenConfirmDeleteDrawer"
        :title="t('components.asset_groups.delete.confirm.title')"
        :description="
          t('components.asset_groups.delete.confirm.description', {
            name: item.name,
          })
        "
        @confirm="handleDeleteAssetGroup(item.id)"
      />
      <Edit
        v-if="item"
        :open="isEditDrawerOpen"
        @update:open="handleOpenEditDrawer"
        @update:group="emit('update:group', $event)"
        @confirm="emit('update:open', false)"
        :item="item"
      />
      <Add
        :open="isAddDrawerOpen"
        @update:open="handleOpenAddDrawer($event)"
        @confirm="emit('update:open', false)"
        :item="item"
      />
    </template>
  </ResponsiveModal>
</template>

<script setup>
import { computed, inject, ref } from "vue";
import { storeToRefs } from "pinia";

import { ResponsiveModal } from "@/SharedComponents/ResponsiveModal";
import { Button } from "@/SharedComponents/Buttons";
import { Icon } from "@/SharedComponents/Icon";
import { DeleteModal as Delete } from "@/SharedComponents/Modals";
import { Add, Edit } from "@/Pages/AssetGroups/Partials/Drawers";

import { useFilters } from "@/Composables/useFilters";
import { useSites } from "@/Composables/useSites";
import { useI18n } from "vue-i18n";
import { isEmpty } from "lodash";

// Translations
const { t } = useI18n();

// Define Emits
const emit = defineEmits(["delete:group", "duplicate:group", "update:group"]);

// Filters: Global
const globalFilterStore = useFilters("global");
const { filterValues: globalFilterValues } = storeToRefs(globalFilterStore);

// Buildings
const { isLoading: isLoadingBuildings, data: buildingsData } = useSites();

// Permissions
const { hasPermission, permissions } = inject("permissions");

// Refs
const isConfirmDeleteDrawerOpen = ref(false);
const isEditDrawerOpen = ref(false);
const isAddDrawerOpen = ref(false);

// Computed props
const buildingId = computed(() => globalFilterValues.value.tenant?.[0] || "");

const buildings = computed(() => {
  if (isLoadingBuildings.value) {
    return [];
  }

  return buildingsData.value.filter((building) =>
    hasPermission(permissions.createAsset, building.id)
  );
});

const canCreateAssetGroups = computed(() => {
  if (!buildingId.value) {
    return !isEmpty(buildings.value);
  }

  return hasPermission(permissions.createAsset, buildingId.value);
});

const canUpdateAssetGroups = computed(() => {
  if (!buildingId.value) {
    return !isEmpty(buildings.value);
  }

  return hasPermission(permissions.updateAsset, buildingId.value);
});

const canDeleteAssetGroups = computed(() => {
  if (!buildingId.value) {
    return !isEmpty(buildings.value);
  }

  return hasPermission(permissions.deleteAsset, buildingId.value);
});

const handleOpenConfirmDeleteDrawer = (value) => {
  isConfirmDeleteDrawerOpen.value = value;
};

const handleOpenEditDrawer = (value) => {
  isEditDrawerOpen.value = value;
};

const handleOpenAddDrawer = (value) => {
  isAddDrawerOpen.value = value;
};

const handleDeleteAssetGroup = (id) => {
  isConfirmDeleteDrawerOpen.value = false;

  emit("delete:group", id);
};

defineProps({
  item: { type: Object, required: false },
});
</script>
